/**
 * VyjimkaVyrizeniModal — settling one item of the payments queue (P6-e2; admin): either accept it as handled (kept, or settled outside
 * the system — no money moves) or refund what it still owes through the gateway. The server decides how much is left to refund and makes
 * the refund idempotent (a double click or a retry cannot refund twice), so this dialog only sends the choice and a note. A refund the
 * gateway has not confirmed leaves the item open: the dialog says so, and repeating the action settles it. Nothing can dismiss it while the
 * request is in flight.
 */

import { useState } from 'react';
import { Button } from '@/components/Button';
import { Field, Fields } from '@/components/Field';
import { Modal, DialogCrown } from '@/components/Modal';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import type { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import { useVyresitVyjimkuMutation, type PlatbaVyjimka } from './api';
import { DRUH_VYJIMKY, POZNAMKA_MAX, providerNazev } from './platby-helpers';

interface VyjimkaVyrizeniModalProps {
  vyjimka: PlatbaVyjimka;
  akce: 'prijato' | 'vraceno';
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VyjimkaVyrizeniModal({ vyjimka, akce, open, onOpenChange }: VyjimkaVyrizeniModalProps) {
  const vyresit = useVyresitVyjimkuMutation(vyjimka.id);
  const [poznamka, setPoznamka] = useState('');
  const busy = vyresit.isPending;
  const druh = DRUH_VYJIMKY[vyjimka.druh];
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };

  const submit = () => {
    if (busy) return;
    vyresit.mutate(
      { akce, poznamka: poznamka.trim() },
      {
        onSuccess: (res) => {
          if (res.stav === 'ceka') toast.warn('Vrácení brána ještě nepotvrdila, položka zůstává otevřená. Vyřiďte ji znovu později.');
          else toast.ok(akce === 'vraceno' ? 'Peníze vráceny, položka vyřízena.' : 'Položka vyřízena.');
          onOpenChange(false);
        },
        onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se položku vyřídit.'}`),
      },
    );
  };

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={560}>
      <DialogCrown
        eyebrow={druh.label}
        eyebrowMeta={<span>Rezervace № {vyjimka.id_rezervace}</span>}
        title={akce === 'vraceno' ? <>Vrátit <em>zbývající částku</em></> : <>Označit jako <em>vyřízené</em></>}
        sub={`${providerNazev(vyjimka.provider)} · platba № ${vyjimka.id_platby}${vyjimka.zakaznik_nazev ? ` · ${vyjimka.zakaznik_nazev}` : ''}`}
      />
      <Modal.Body className="px-8 py-5">
        <form
          id="vyjimka-form"
          data-testid="vyjimka-form"
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <p className="text-[12.5px] leading-relaxed text-ink-2">{druh.popis}</p>
          <p data-testid="vyjimka-nasledek" className="text-[12.5px] leading-relaxed text-ink-2">
            {akce === 'vraceno'
              ? `Vrátí se to, co z položky ještě zbývá (nejvýše ${formatInt(vyjimka.castka)} Kč), přes platební bránu na zdroj platby. Vrácení nelze vzít zpět.`
              : 'Položka se uzavře a žádné peníze se nepohnou — použijte to, když je částka ponechána nebo vyřešena mimo systém.'}
          </p>
          <Fields cols={1}>
            <Field label="Poznámka" optional="volitelné" hint="Uloží se do historie platby.">
              <TextInput value={poznamka} maxLength={POZNAMKA_MAX} disabled={busy} data-testid="vyjimka-poznamka" onChange={(e) => setPoznamka(e.currentTarget.value)} />
            </Field>
          </Fields>
        </form>
      </Modal.Body>
      <Modal.Foot
        actions={
          <>
            <Button variant="secondary" onClick={() => guarded(false)} disabled={busy}>
              Zrušit
            </Button>
            <Button variant="ink" type="submit" form="vyjimka-form" disabled={busy} data-testid="vyjimka-potvrdit">
              {busy ? 'Ukládám…' : akce === 'vraceno' ? 'Vrátit peníze' : 'Označit jako vyřízené'}
            </Button>
          </>
        }
      />
    </Modal>
  );
}
